import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "context/TranslationProvider";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "../components/Pagination";
import {
  Button,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import helpers from "utils/helpers";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate } from "react-router-dom";
import DropdownFilter from "utils/DropdownFilter";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "context/AuthContext";

function BroadcastsTable({ items, deleteRow, onSend, onStatusChange }) {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchText, setSearchText] = useState("");
  const componentRef = useRef(null);
  const [targetFilter, setTargetFilter] = useState("all");
  const handleClearFilters = () => {
    setSearchText("");
    setTargetFilter("all");
    setCurrentPage(0);
  };
  const { permission } = useAuth();
  const broadcastPermission = permission?.permissions?.modules?.broadCasts;
  const canCreate = broadcastPermission?.create;
  const canUpdate = broadcastPermission?.update;
  const canDelete = broadcastPermission?.delete;

  const filteredItems = useMemo(() => {
    let baseItems = items;
    if (targetFilter !== "all") {
      baseItems = items.filter((item) => {
        if (targetFilter === "roles") {
          return item.targetRoles?.length > 0;
        }

        if (targetFilter === "users") {
          return item.targetUsers?.length > 0;
        }

        if (targetFilter === "all") {
          return !item.targetRoles?.length && !item.targetUsers?.length;
        }

        return true;
      });
    }

    const searchString = searchText.toLowerCase();
    return baseItems.filter((item) => {
      const title = (item.title || "").toLowerCase();
      const status = (item.status || "").toLowerCase();
      const createdBy = item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}`.toLowerCase() : "";
      
      return (
        title.includes(searchString) ||
        status.includes(searchString) ||
        createdBy.includes(searchString)
      );
    });
  }, [items, targetFilter, searchText]);

  const paginatedItems = useMemo(() => {
     return filteredItems.slice(
       currentPage * rowsPerPage,
       (currentPage + 1) * rowsPerPage
     );
  }, [filteredItems, currentPage, rowsPerPage]);

  const dataTable = filteredItems || [];

  const renderTarget = (row) => {
    if (row.targetRoles?.length) {
      return (
        row.targetRoles
          ?.map((role) => t(helpers.ROLE_LABELS[role] || role))
          .join(", ") || "-"
      );
    }

    if (row.targetUsers?.length) {
      return `${row.targetUsers.length} ${t("broadcasts.targets.users", "Users")}`;
    }

    return t("broadcasts.targets.all", "All users");
  };

  return (
    <ToolkitProvider
      data={dataTable}
      key={language}
      keyField="_id"
      columns={[
        {
          dataField: "title",
          text: t("table.headers.title", "Title"),
          sort: true,
          headerAlign: "left",
          align: "left",
        },
        {
          dataField: "target",
          text: t("broadcasts.target", "Target"),
          headerAlign: "left",
          align: "left",
          formatter: (_, row) => renderTarget(row),
        },
        {
          dataField: "status",
          text: t("table.headers.status", "Status"),
          sort: true,
          headerAlign: "left",
          align: "left",
          formatter: (_, row) => {
            if (!canUpdate) return t(`broadcasts.status.${row.status}`);
            return (
              <UncontrolledDropdown>
                <DropdownToggle caret size="sm" key={`broadcast-status-${row._id}-${language}`} className="text-capitalize">
                  {t(`broadcasts.status.${row.status}`)}
                </DropdownToggle>

                <DropdownMenu>
                  {[helpers.BROADCAST_FILTERS.SENT, helpers.BROADCAST_FILTERS.DRAFT].map((status) => (
                    <DropdownItem key={status} disabled={status === row.status} onClick={() => onStatusChange(row, status)}>
                      {t(`broadcasts.status.${status}`)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            );
          },
        },
        {
          dataField: "createdBy",
          text: t("broadcasts.createdBy", "Created By"),
          headerAlign: "left",
          align: "left",
          formatter: (cell) => (
            <span id={`creator-${cell?._id}`}>
              {cell?.firstName} {cell?.lastName}
              <UncontrolledTooltip target={`creator-${cell?._id}`}>
                {cell?.email}
              </UncontrolledTooltip>
            </span>
          ),
        },
        {
          dataField: "scheduledAt",
          text: t("broadcasts.scheduledAt", "Scheduled At"),
          headerAlign: "left",
          align: "left",
          formatter: (cell) => {
            if (!cell) {
              return (
                  "N/A"
              );
            }

            const dateObj = new Date(cell);

            const formattedDate = helpers.getFormattedDate(cell, t);

            const formattedTime = dateObj.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });

            return `${formattedDate} ${formattedTime}`;
          },
        },
        {
          dataField: "createdAt",
          text: t("table.headers.createdAt", "Created At"),
          headerAlign: "left",
          align: "left",
          sort: true,
          formatter: (cell) => helpers.getFormattedDate(cell, t),
        },
        {
          dataField: "actions",
          text: t("table.headers.actions", "Actions"),
          headerAlign: "left",
          align: "left",
          formatter: (_, row) => {
            return (
              <div className="d-flex align-items-center gap-1">
                {canUpdate && (
                  <>
                    <Button size="sm" id={`edit-${row._id}`} onClick={() => navigate(`/admin/broadcasts/edit/${row._id}`)}>
                      <EditIcon fontSize="small" color="info" />
                    </Button>
                    <UncontrolledTooltip target={`edit-${row._id}`} key={`edit-${row._id}-${language}`}>{t("broadcasts.edit", "Edit Broadcast")}</UncontrolledTooltip>

                    <Button size="sm" id={`send-${row._id}`} onClick={() => onSend(row)}>
                      {row.status === helpers.BROADCAST_FILTERS.DRAFT ? (
                        <SendIcon fontSize="small" color="primary" />
                      ) : (
                        <ReplayIcon fontSize="small" color="primary" />
                      )}
                    </Button>
                    <UncontrolledTooltip target={`send-${row._id}`} key={`send-${row._id}-${language}`}>
                      {row.status === helpers.BROADCAST_FILTERS.DRAFT ? t("broadcasts.send", "Send Broadcast") : t("broadcasts.resend", "Resend Broadcast")}
                    </UncontrolledTooltip>
                  </>
                )}
                {canDelete && (
                  <Button id={`delete-${row._id}`} size="sm" onClick={() => deleteRow(row)}>
                    <DeleteIcon fontSize="small" color="error" />
                  </Button>
                )}
                {canDelete && (
                  <UncontrolledTooltip target={`delete-${row._id}`} key={`delete-${row._id}-${language}`}>{t("broadcasts.delete", "Delete Broadcast")}</UncontrolledTooltip>
                )}
              </div>
            );
          },
        },
      ]}
      search
    >
      {(props) => (
        <div className="py-4 w-100 d-flex flex-column" ref={componentRef}>
          <div
            id="datatable-basic_filter"
            className="dataTables_filter px-4 pb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
          >

            <div className="d-flex align-items-center mb-2 mb-md-0">
              <span className="text-sm font-medium text-slate-600 mr-2">
                {t("table.search", "Search")}:
              </span>
              <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                <input
                  className="form-control form-control-sm"
                  placeholder={t("table.search", "Search")}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(0);
                  }}
                  style={{ paddingRight: "24px" }}
                />
                {searchText && (
                  <span
                    onClick={() => {
                      setSearchText("");
                      setCurrentPage(0);
                    }}
                    className="position-absolute cursor-pointer search-clear-btn"
                  >
                    <CloseIcon style={{ fontSize: "14px" }} />
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center">
              {canCreate && (
                <Button
                  color="warning"
                  size="sm"
                  className="mb-2 mb-sm-0 mr-sm-2 w-100 w-sm-auto"
                  onClick={() => navigate("/admin/broadcasts/create")}
                >
                  <AddIcon fontSize="small" className="mr-1" />
                  {t("broadcasts.create", "Create a New Broadcast")}
                </Button>
              )}

              <div className="w-100 w-sm-auto d-flex align-items-center">
                <DropdownFilter
                  label={t("table.filterBy", "Filter By")}
                  value={targetFilter}
                  options={helpers.getBroadcastTargetFilter(t)}
                  onChange={(val) => {
                    setTargetFilter(val);
                    setCurrentPage(0);
                  }}
                />
                <Button
                  size="sm"
                  className="ml-2 btn-neutral text-danger font-weight-600"
                  onClick={handleClearFilters}
                >
                  {t("common.clearFilters", "Clear Filters")}
                </Button>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <BootstrapTable
              {...props.baseProps}
              data={paginatedItems}
              bootstrap4
              bordered={false}
              wrapperClasses="max-h-[60vh] overflow-y-auto"
              noDataIndication={() => (
                <div className="text-center text-muted py-4">
                  {t("broadcasts.noBroadcasts", "No broadcasts found")}
                </div>
              )}
            />
          </div>
          <Pagination
            totalRows={filteredItems.length}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onRowsPerPageChange={(size) => setRowsPerPage(size)}
            t={t}
          />
        </div>
      )}
    </ToolkitProvider>
  );
}

export default BroadcastsTable;
